import { debouncer, getRequest, throttle } from '@/Util/utils';
import { addDays, daysBetween, LocalDate, midnight } from '@/time';
import route from '@/Util/routes';
import { addWeeks, differenceInWeeks, addMonths, subMonths, startOfDay } from 'date-fns';
import { Activity, type Exercise } from '@/Common/ActivityFunctions';

export interface LoaderActivity {
  isStale: boolean;
  activity: Exercise;
}

type Range = {
  start: Date;
  end: Date;
}

const loadAheadActivities: number = 70;
const dataPersistActivities: number = 140;
const loadAheadStats: number = 3;
const dataPersistStats: number = 140;

export class CalendarLoader {
  private activitiesReactivity: () => void;
  private statsReactivity: () => void;

  private activities: { [date: string]: LoaderActivity[] } = {};
  private stats = {};
  private trainingStatuses = {};

  private activitiesLoadedRange: Range = { start: null, end: null };
  private activitiesRequestedRange: Range = { start: null, end: null };
  private activitiesRequestedReloadDate: Date = null;
  private statsLoadedRange: Range = { start: null, end: null };
  private statsRequestedRange: Range = { start: null, end: null };
  private statsRequestedReloadDate: Date = null;

  private activitiesThrottle = throttle();
  private activitiesDebouncer = debouncer(200);
  private executeActivitiesDebouncer = () => this.activitiesDebouncer.debounce(() => {
    this.activitiesThrottle.throttle(() => {
      return this.loadActivities();
    });
  });
  private statsThrottle = throttle();
  private statsDebouncer = debouncer(200);
  private executeStatsDebouncer = () => this.statsDebouncer.debounce(() => {
    this.statsThrottle.throttle(() => {
      return this.loadWeeklyStats();
    });
  });

  public constructor(
    activitiesReactivity: () => void,
    statsReactivity: () => void,
  ) {
    this.activitiesReactivity = activitiesReactivity;
    this.statsReactivity = statsReactivity;
  }

  public load(start: Date = null, end: Date = null) {
    if (start && end) {
      this.updateActivitiesRequestedRange(start, end);
      this.updateStatsRequestedRange(start, end);
    }
    this.executeActivitiesDebouncer();
    this.executeStatsDebouncer();
  }

  public reloadFromEarliest(dates: Date[]) {
    if (dates.length == 0) return;
    let lower = dates[0];
    dates.forEach(d => {
      if (d < lower) lower = d;
    })
    this.reloadFrom(lower);
  }

  public reloadFrom(date: Date) {
    if (this.activitiesRequestedReloadDate == null || date < this.activitiesRequestedReloadDate) {
      this.activitiesRequestedReloadDate = startOfDay(date);
    }
    this.executeActivitiesDebouncer();
    this.executeStatsDebouncer();
  }

  private async loadActivities() {
    this.deleteOutOfRangeActivities();

    if (this.activitiesRequestedReloadDate) {
      // Mark activities after the reload date as stale so that they can be removed after the API call completes.
      for (let key in this.activities) {
        if (LocalDate.toDate(key) >= this.activitiesRequestedReloadDate) {
          for (const e of this.activities[key]) {
            if (e) e.isStale = true
          }
        }
      }
      this.activitiesReactivity();
    }

    const datesToLoad = CalendarLoader.getDateRangeToLoad(this.activitiesRequestedRange, this.activitiesLoadedRange, this.activitiesRequestedReloadDate);
    this.activitiesRequestedReloadDate = null;
    if (!datesToLoad) return;
    const start: Date = datesToLoad.start;
    const end: Date = datesToLoad.end;

    let activities = await window.fetch('/calendar/events?' + new URLSearchParams({
      start: start.toISOString(),
      end: end.toISOString(),
      includeMatched: 'true',
      includeDuplicates: 'true',
      includeHidden: 'true',
    }), {
      method: 'GET',
      ...getRequest()
    }).then(r => r.json())

    this.deleteStaleActivities();

    // Remove loaded activities from dates of activities that have just been loaded in.
    activities.forEach(a => {
      let key = LocalDate.format(new Date(a.start_date));
      if (key in this.activities) {
        delete this.activities[key]
      }
    });
    // Populate date indexed dictionary of activities.
    activities.forEach(a => {
      let key = LocalDate.format(new Date(a.start_date));
      if (key in this.activities) {
        this.activities[key].push({ isStale: false, activity: a });
      } else {
        this.activities[key] = [{ isStale: false, activity: a }];
      }
    });

    CalendarLoader.updateLoadedRange(this.activitiesLoadedRange, { start, end });

    this.activitiesReactivity();
  }

  private async loadWeeklyStats() {
    this.deleteOutOfRangeStats();

    const datesToLoad = CalendarLoader.getDateRangeToLoad(this.statsRequestedRange, this.statsLoadedRange, this.statsRequestedReloadDate);
    this.statsRequestedReloadDate = null;
    if (!datesToLoad) return;
    const start: Date = datesToLoad.start;
    const end: Date = datesToLoad.end;

    const timezone = (new Intl.DateTimeFormat()).resolvedOptions().timeZone
    const numWeeks = Math.ceil(daysBetween(start, end) / (7));
    const [stats, statuses] = await Promise.all([
      window.fetch(
        '/calendarSummaryWeekly?' + new URLSearchParams({
          theDate: start.toISOString(),
          // timezoneOffset: `${loadStart.getTimezoneOffset()}`, 
          // Pass actual browser timezone so that returned dates are accurate.
          tz: timezone,
          numRows: `${numWeeks + 1}`
        }), {
        method: 'GET', ...getRequest()
      }).then(r => r.json()).then(v => v.data)
      ,
      // TODO: pass timeszone to getCalendarTrainingStatus like with calendarSummary above.
      // Currently it assumes $user->timezone.
      window.fetch(
        route('ActivityController@getCalendarTrainingStatus', [
          LocalDate.format(start), LocalDate.format(end)
        ]), {
        method: 'GET',
        ...getRequest()
      }).then((r) => r.json())
    ])

    // Remove loaded statuses from dates of statuses that have just been loaded in.
    statuses.forEach(s => {
      let key: string = s.date;
      if (key in this.trainingStatuses) {
        delete this.trainingStatuses[key]
      }
    });

    // Populate date indexed dictionaries of stats and statuses.
    stats.forEach(s => {
      let key: string = s.datestart;
      this.stats[key] = s;
    });
    statuses.forEach(s => {
      let key: string = s.date;
      if (!this.trainingStatuses[key]) this.trainingStatuses[key] = [s];
      else this.trainingStatuses[key].push(s);
    });

    CalendarLoader.updateLoadedRange(this.statsLoadedRange, { start, end });

    this.statsReactivity();
  }

  private updateActivitiesRequestedRange(start: Date, end: Date) {
    start = addDays(start, -loadAheadActivities);
    end = addDays(end, loadAheadActivities);
    // Round to nearest month to reduce number of loads required as user scrolls.
    const from = new Date(start.getFullYear(), start.getMonth(), 1);
    const to = new Date(end.getFullYear(), end.getMonth() + 1, 0);
    this.activitiesRequestedRange = {
      start: from,
      end: to,
    }
  }

  private updateStatsRequestedRange(start: Date, end: Date) {
    start = addDays(start, -loadAheadStats);
    end = addDays(end, loadAheadStats);
    // Round to nearest month to reduce number of loads required as user scrolls.
    const from = new Date(start.getFullYear(), start.getMonth(), 1);
    const to = new Date(end.getFullYear(), end.getMonth() + 1, 0);
    this.statsRequestedRange = {
      start: from,
      end: to,
    }
  }

  private static getDateRangeToLoad(requestedRange: Range, loadedRange: Range, requestedReloadDate: Date): Range {
    // If no requested dates, nothing to do.
    if (!requestedRange.start || !requestedRange.end) {
      return null;
    }

    let start: Date = null;
    let end: Date = null;

    // if loaded range is unset, nothing to reload, simply load requested range
    if (!loadedRange.start || !loadedRange.end) {
      start = requestedRange.start;
      end = requestedRange.end;
    } else {
      // If the requested reload date is after the currently loaded range, then no need to reload anything.
      if (requestedReloadDate && loadedRange.end < requestedReloadDate) {
        requestedReloadDate = null;
      }
  
      if (requestedReloadDate) {
        let start = requestedReloadDate;
        // If requested reload date is before currently requested range, start reload from the currently requested range.
        // We don't need to load activities before the currently requested range.
        if (start < requestedRange.start) {
          start = requestedRange.start;
        }
        let end = loadedRange.end;
        // If the requested range is farther than the currently loaded range, we might as well load up to
        // the requested range.
        if (end < requestedRange.end) {
          end = requestedRange.end;
        }
      } else {
        // If requested range entirely outside of loaded range, load entire requested range.
        if (requestedRange.end < loadedRange.start || loadedRange.end < requestedRange.start) {
          start = requestedRange.start;
          end = requestedRange.end;
        }
        // If requested range entirely overlaps loaded range, load entire requested range.
        else if (requestedRange.start < loadedRange.start && requestedRange.end > loadedRange.end) {
          start = requestedRange.start;
          end = requestedRange.end;
        }
        // If requested start is less than loaded start, extend loaded range backwards to requested start.
        else if (requestedRange.start < loadedRange.start) {
          start = requestedRange.start;
          end = loadedRange.start;
        }
        // If requested end is greater than loaded end, extend loaded range forwards to requested end.
        else if (loadedRange.end < requestedRange.end) {
          start = loadedRange.end;
          end = requestedRange.end;
        }
        // Requested range is entirely within loaded range, nothing to load.
        else {
          return null;
        }
      }
    }

    return { start, end };
  }

  private static updateLoadedRange(loadedRange: &Range, newlyLoadedRange: Range) {
    if (!loadedRange.start || !loadedRange.end) {
      loadedRange = newlyLoadedRange;
    }
    // If newly loaded range entirely outside of loaded range, set loaded range to the newly loaded range.
    else if (newlyLoadedRange.end < loadedRange.start || loadedRange.end < newlyLoadedRange.start) {
      loadedRange = newlyLoadedRange;
    }
    // If newly loaded range entirely overlaps loaded range, set loaded range to the newly loaded range.
    else if (newlyLoadedRange.start < loadedRange.start && newlyLoadedRange.end > loadedRange.end) {
      loadedRange = newlyLoadedRange;
    }
    // If newly loaded start is less than loaded start, extend loaded range backwards to newly loaded start.
    else if (newlyLoadedRange.start < loadedRange.start) {
      loadedRange.start = newlyLoadedRange.start;
    }
    // If newly loaded end is greater than loaded end, extend loaded range forwards to newly loaded end.
    else if (loadedRange.end < newlyLoadedRange.end) {
      loadedRange.end = newlyLoadedRange.end;
    }
  }

  private deleteOutOfRangeActivities() {
    if (!this.activitiesRequestedRange?.start || !this.activitiesRequestedRange?.end) return;
    const start = addDays(this.activitiesRequestedRange.start, -(dataPersistActivities - loadAheadActivities));
    const end = addDays(this.activitiesRequestedRange.end, dataPersistActivities - loadAheadActivities);
    for (let key in this.activities) {
      const date = new Date(key);
      if (date < start || date > end) {
        delete this.activities[key]
      }
    }
    if (this.activitiesLoadedRange?.start && this.activitiesLoadedRange?.end) {
      if (this.activitiesLoadedRange.start < start) this.activitiesLoadedRange.start = start;
      if (end < this.activitiesLoadedRange.end) this.activitiesLoadedRange.end = end;
    }
  }

  private deleteOutOfRangeStats() {
    if (!this.statsRequestedRange?.start || !this.statsRequestedRange?.end) return;
    const start = addDays(this.statsRequestedRange.start, -(dataPersistStats - loadAheadStats));
    const end = addDays(this.statsRequestedRange.end, dataPersistStats - loadAheadStats);
    for (let key in this.stats) {
      const date = new Date(key);
      if (date < start || date > end) {
        delete this.stats[key]
      }
    }
    for (let key in this.trainingStatuses) {
      const date = new Date(key);
      if (date < start || date > end) {
        delete this.trainingStatuses[key]
      }
    }
    if (this.statsLoadedRange?.start && this.statsLoadedRange?.end) {
      if (this.statsLoadedRange.start < start) this.statsLoadedRange.start = start;
      if (end < this.statsLoadedRange.end) this.statsLoadedRange.end = end;
    }
  }

  private deleteStaleActivities() {
    for (let key in this.activities) {
      const date = new Date(key);
      const activities = this.activities[key];
      this.activities[key] = activities.filter((la) => !la.isStale)
      if (this.activities[key].length == 0) {
        delete this.activities[key]
      }
    }
  }

  public swapActivities(activityA: Exercise, activityB: Exercise) {
    const dateA = activityA.start_date
    const dateB = activityB.start_date

    this.moveActivity(activityA, dateB)
    this.moveActivity(activityB, dateA)

    const keyA = LocalDate.format(new Date(dateB))
    const loaderActivityA = this.activities[keyA].find((la) => la.activity.path == activityA.path)
    // This should never be null, but just in case the move failed somehow check anyway
    if (loaderActivityA) {
      loaderActivityA.activity.original_placeholder_date = loaderActivityA.activity.original_placeholder_date ?? dateA;
    }
    const keyB = LocalDate.format(new Date(dateA))
    const loaderActivityB = this.activities[keyB].find((la) => la.activity.path == activityB.path)
    // This should never be null, but just in case the move failed somehow check anyway
    if (loaderActivityB) {
      loaderActivityB.activity.original_placeholder_date = loaderActivityB.activity.original_placeholder_date ?? dateB;
    }

    this.activitiesReactivity()
  }

  public moveActivity(activity: Exercise, newDate) {
    this._moveActivity(activity, newDate)
    this.activitiesReactivity()
  }

  private _moveActivity(activity: Exercise, newDate) {
    const key = LocalDate.format(new Date(activity.start_date))
    const loaderActivity = this.activities[key].find((la) => la.activity.path == activity.path)
    this.activities[key] = this.activities[key].filter((la) => la.activity.path != activity.path)

    this._copyActivity(loaderActivity, newDate)
  }

  public copyActivity(activity: Exercise, newDate) {
    const key = LocalDate.format(new Date(activity.start_date))
    const loaderActivity = this.activities[key].find((la) => la.activity.path == activity.path)
    this._copyActivity(loaderActivity, newDate)
    this.activitiesReactivity()
  }

  private _copyActivity(loaderActivity: LoaderActivity, newDate) {
    const newKey = LocalDate.format(new Date(newDate))
    loaderActivity.activity.start_date = new Date(newDate).toISOString();
    if (newKey in this.activities) {
      this.activities[newKey].push(loaderActivity);
    } else {
      this.activities[newKey] = [loaderActivity];
    }
  }

  // This can be used to update activity fields other than the start_date
  public updateActivity(activity: Exercise, updater: (activity: Exercise) => Exercise, setStale: boolean = false) {
    const key = LocalDate.format(new Date(activity.start_date))
    const loaderActivity = this.activities[key].find((a) => a.activity.path == activity.path)
    if (loaderActivity) {
      loaderActivity.activity = updater(loaderActivity.activity)
      if (setStale) {
        loaderActivity.isStale = true;
      }
      this.activitiesReactivity();
    }
  }

  public removeActivity(activity: Exercise) {
    if (Activity.isForecast(activity)) return;
    const key = LocalDate.format(new Date(activity.start_date))
    this.activities[key] = this.activities[key].filter((la) => la.activity.path !== activity.path).map((la) => {
      if (Activity.isMatchedAtoB(activity, la.activity)) {
        return { ...la, activity: { ...la.activity, num_matches: Math.max(0, la.activity.num_matches - 1) } };
      }
      return la;
    });
    if (this.activities[key].length == 0) {
      delete this.activities[key]
    }
    this.activitiesReactivity();
  }

  public getActivities() {
    return this.activities;
  }

  public getActivitiesForDate(date: Date) {
    const key = LocalDate.format(date);
    return this.activities[key] || [];
  }

  public getStats() {
    return [this.stats, this.trainingStatuses];
  }

  public clearStats() {
    this.stats = {};
    this.trainingStatuses = {};
    this.statsLoadedRange = { start: null, end: null };
    this.statsRequestedReloadDate = null;
    this.statsReactivity();
  }
}
